import { TextField } from "@mui/material";
import { useField, useFormikContext } from "formik";
import { memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * Text field that keeps typing in local state and commits to Formik on blur or
 * submit. Avoids notifying the whole form (and every FastField) on each keystroke.
 */
const DeferredFormikTextField = memo(function DeferredFormikTextField({
  name,
  helperText,
  onBlur,
  ...props
}) {
  const [field, meta, helpers] = useField(name);
  const { submitCount } = useFormikContext();
  const [localValue, setLocalValue] = useState(field.value ?? "");
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const hasPendingChangesRef = useRef(false);
  const localValueRef = useRef(localValue);
  localValueRef.current = localValue;

  useEffect(() => {
    if (!hasPendingChangesRef.current) {
      setLocalValue(field.value ?? "");
    }
  }, [field.value]);

  const commitToFormik = useCallback(
    (shouldValidate) => {
      const nextValue = localValueRef.current;
      hasPendingChangesRef.current = false;
      setHasPendingChanges(false);

      if (nextValue !== field.value) {
        helpers.setValue(nextValue, shouldValidate);
      }
    },
    [field.value, helpers],
  );

  const commitRef = useRef(commitToFormik);
  commitRef.current = commitToFormik;

  useLayoutEffect(() => {
    if (hasPendingChangesRef.current) {
      commitRef.current(false);
    }
  }, [submitCount]);

  useLayoutEffect(
    () => () => {
      commitRef.current(false);
    },
    [],
  );

  const handleChange = (event) => {
    hasPendingChangesRef.current = true;
    setHasPendingChanges(true);
    setLocalValue(event.target.value);
  };

  const handleBlur = (event) => {
    const nextValue = localValueRef.current;
    const hasUncommittedValue =
      hasPendingChangesRef.current || nextValue !== field.value;

    if (hasUncommittedValue) {
      hasPendingChangesRef.current = false;
      setHasPendingChanges(false);

      if (nextValue !== field.value) {
        // Validate with the committed value; setFieldTouched would validate stale Formik state.
        helpers.setValue(nextValue, true);
        helpers.setTouched(true, false);
      } else {
        field.onBlur(event);
      }
    } else {
      field.onBlur(event);
    }

    onBlur?.(event);
  };

  const showError = Boolean(meta.touched && meta.error && !hasPendingChanges);

  return (
    <TextField
      {...props}
      name={name}
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      error={showError}
      helperText={showError ? meta.error : helperText ?? null}
    />
  );
});

export default DeferredFormikTextField;
